<template>
  <div>
    <div class="d-flex align-center mb-4">
      <span class="subtitle1"> {{ $t("mspWorkspacePreview.childWorkspaces.title") }} </span>
      <v-chip
        class="ml-3 font-weight-bold"
        color="indigo-instructional"
        text-color="primary"
        size="small"
        variant="flat"
        label
        rounded="pill"
      >
        {{ totalChildren }}
      </v-chip>
    </div>

    <div
      v-if="childWorkspaces.data && childWorkspaces.data.items.length === 0"
      data-testid="child-workspaces-empty-state"
    >
      <div class="border-opacity-100 border border-indigo-faint pa-6 headline6 text-center">
        {{ $t("mspWorkspacePreview.childWorkspaces.emptyState") }}
      </div>
    </div>

    <v-table
      v-else
      class="child-workspaces-table elevation-0 border-opacity-100 border border-indigo-faint"
      hover
      density="default"
    >
      <template #top>
        <v-progress-linear
          :style="{ visibility: childWorkspaces.loading ? 'visible' : 'hidden' }"
          indeterminate
          height="2px"
        />
      </template>
      <thead>
        <tr>
          <th class="text-left caption">
            {{ $t("mspWorkspacePreview.childWorkspaces.headers.workspace") }}
          </th>

          <th class="text-center caption">
            <span v-html="$t('mspWorkspacePreview.childWorkspaces.headers.protectedUsers')"></span>
          </th>

          <th class="text-center caption">
            <span
              v-html="$t('mspWorkspacePreview.childWorkspaces.headers.protectedDevices')"
            ></span>
          </th>

          <th class="text-right caption">
            {{ $t("mspWorkspacePreview.childWorkspaces.headers.tickets") }}
          </th>

          <th :aria-label="$t('ariaLabels.actionsColumn')"></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="item in items" :key="item.workspaceId">
          <td class="py-2">
            <div class="subtitle2">
              {{ item.displayName }}
            </div>
            <div class="d-flex align-center ga-1">
              <v-chip
                v-for="label in item.labels"
                variant="flat"
                :color="label.color"
                :text="label.name"
                :key="label.id"
                label
                rounded="3"
                density="compact"
                size="12"
              >
                <span class="coro-label-sm text-uppercase text-primary px-1">
                  {{ label.name }}
                </span>
              </v-chip>
            </div>
          </td>

          <td class="text-center body2">
            <span
              class="coro-link"
              data-testid="view-protected-users"
              @click="$emit('view-action-clicked', MspWorkspaceAction.VIEW_PROTECTED_USERS, item)"
              >{{ item.protectedUsers }}</span
            >&nbsp;/
            <span>{{ item.protectableUsers }}</span>
          </td>

          <td class="text-center">
            <span
              class="body2 coro-link"
              data-testid="view-devices"
              @click="$emit('view-action-clicked', MspWorkspaceAction.VIEW_DEVICES, item)"
            >
              {{ item.protectedDevices }}
            </span>
          </td>

          <td class="text-right">
            <span
              class="subtitle2 coro-link text-red-dark"
              data-testid="view-tickets"
              @click="$emit('view-action-clicked', MspWorkspaceAction.VIEW_TICKETS, item)"
            >
              {{ item.ticketsCount }}
            </span>
          </td>

          <td class="text-right px-4">
            <v-btn
              icon
              variant="text"
              size="small"
              density="compact"
              data-testid="view-workspace"
              @click="$emit('view-action-clicked', MspWorkspaceAction.VIEW_WORKSPACE, item)"
            >
              <v-icon icon="$chevronRight"></v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>

    <div v-if="totalChildren > PAGE_SIZE && items.length < totalChildren" class="text-center pa-4">
      <div
        class="body2 coro-link text-center"
        data-testid="load-more-child-workspaces"
        @click="onLoadMoreClicked()"
      >
        + {{ $t("general.workspaces", { n: remainingWorkspaces }, remainingWorkspaces) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import type { DataValue, Paged } from "@/types.ts";
import type { MspWorkspaceListItem } from "@/_store/msp/msp.module.ts";
import { MspWorkspaceAction } from "@/constants/workplaces.ts";

const PAGE_SIZE = 20;

export default defineComponent({
  props: {
    childWorkspaces: {
      type: Object as PropType<DataValue<Paged<MspWorkspaceListItem>>>,
      required: true,
    },
  },
  emits: {
    "load-more-child-workspaces": (_page: number) => true,
    "view-action-clicked": (_action: MspWorkspaceAction, _item: MspWorkspaceListItem) => true,
  },
  setup(props, { emit }) {
    const page = ref(0);

    const items = computed(() => props.childWorkspaces.data?.items ?? []);
    const totalChildren = computed(() => props.childWorkspaces.data?.total ?? 0);
    const remainingWorkspaces = computed(() => totalChildren.value - items.value.length);

    const onLoadMoreClicked = () => {
      page.value += 1;
      emit("load-more-child-workspaces", page.value);
    };

    return {
      PAGE_SIZE,
      page,
      items,
      totalChildren,
      remainingWorkspaces,
      onLoadMoreClicked,
      MspWorkspaceAction,
    };
  },
});
</script>

<style lang="scss" scoped>
.child-workspaces-table {
  --v-table-header-height: 56px;
}
</style>
